'use client';

import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SearchFilters {
  query: string;
  era?: string;
  topic?: string;
  certainty?: string;
  page?: number;
  pageSize?: number;
}

export interface SearchResult {
  id: string;
  title: string;
  excerpt: string | null;
  body_snippet: string | null;
  certainty_label: string;
  era_id: string | null;
  topic_id: string | null;
  region: string | null;
  word_count: number;
  created_at: string;
  author_name: string | null;
  is_anonymous: boolean;
  status: string;
  corroboration_count?: number;
  type: 'contribution';
}

export interface WallSearchResult {
  id: string;
  era_id: string;
  era_label: string;
  topic_id: string;
  topic_label: string;
  published: number;
  min_coverage: number;
  type: 'wall';
}

export interface ForumPostResult {
  id: string;
  era_id: string;
  era_label: string;
  title: string;
  body_snippet: string;
  author_name: string | null;
  upvote_count: number;
  reply_count: number;
  created_at: string;
  type: 'forum';
}

export interface AMAQuestionResult {
  id: string;
  ama_id: string;
  ama_title: string;
  era_id: string | null;
  era_label: string | null;
  question: string;
  answer: string | null;
  answered_at: string | null;
  upvote_count: number;
  is_featured: boolean;
  asker_name: string | null;
  created_at: string;
  type: 'ama';
}

export interface ContributorResult {
  id: string;
  full_name: string;
  username: string | null;
  avatar_url: string | null;
  bio: string | null;
  role: string;
  tier: string;
  reputation_score: number;
  published_count: number;
  specialisations: string[];
  type: 'contributor';
}

export type AnySearchResult = SearchResult | ForumPostResult | AMAQuestionResult | ContributorResult | WallSearchResult;

// ─── Constants ────────────────────────────────────────────────────────────────

export const ERA_LABELS: Record<string, string> = {
  'era-1': 'Early Iron Age',
  'era-2': 'Kingdom of Mutapa',
  'era-3': 'Rozvi Empire',
  'era-4': 'Colonial Contact',
  'era-5': 'Anti Colonial Resistance',
  'era-6': 'Nationalist Period',
  'era-7': 'Post-Independence',
};

export const CERTAINTY_OPTIONS = [
  { value: 'oral_tradition', label: 'Oral Tradition' },
  { value: 'sourced_claim', label: 'Sourced Claim' },
  { value: 'firsthand_account', label: 'Firsthand Account' },
];

export const SEARCH_TABS = [
  { key: 'contributions', label: 'Contributions' },
  { key: 'forum', label: 'Forum Posts' },
  { key: 'ama', label: 'AMA Questions' },
  { key: 'contributors', label: 'Contributors' },
  { key: 'wall', label: 'Wall Topics' },
] as const;

export type SearchTab = typeof SEARCH_TABS[number]['key'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildFtsQuery(query: string): string {
  return query.trim().split(/\s+/).filter(Boolean).map(w => `${w}:*`).join(' & ');
}

// ─── Contributions ────────────────────────────────────────────────────────────

export async function searchContributions(filters: SearchFilters): Promise<{ data: SearchResult[]; total: number }> {
  const supabase = createClient();
  const { query, era, topic, certainty, page = 1, pageSize = 12 } = filters;
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let q = supabase
    .from('contributions')
    .select('id, title, excerpt, body, certainty_label, era_id, topic_id, region, word_count, created_at, is_anonymous, author_id, status', { count: 'exact' })
    .eq('status', 'published')
    .range(from, to);

  if (query.trim()) {
    const fts = buildFtsQuery(query);
    q = q.or(`title.ilike.%${query}%,excerpt.ilike.%${query}%,body.ilike.%${query}%`);
    // Order by recency + relevance (trending: recent + corroboration proxy via word_count)
    q = q.order('created_at', { ascending: false });
  } else {
    q = q.order('created_at', { ascending: false });
  }

  if (era) q = q.eq('era_id', era);
  if (topic) q = q.eq('topic_id', topic);
  if (certainty) q = q.eq('certainty_label', certainty);

  const { data, error, count } = await q;

  if (error) {
    console.error('searchContributions error:', error.message);
    return { data: [], total: 0 };
  }

  const rows = data || [];
  const authorIds = [...new Set(rows.filter((r: any) => !r.is_anonymous && r.author_id).map((r: any) => r.author_id))];
  let profileMap: Record<string, string> = {};
  if (authorIds.length > 0) {
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('id, full_name, username')
      .in('id', authorIds);
    for (const p of profiles || []) {
      profileMap[p.id] = p.full_name || p.username || 'Unknown';
    }
  }

  // Fetch corroboration counts for trending score
  const contribIds = rows.map((r: any) => r.id);
  let corrobMap: Record<string, number> = {};
  if (contribIds.length > 0) {
    const { data: corrobs } = await supabase
      .from('corroborations')
      .select('contribution_id')
      .in('contribution_id', contribIds)
      .eq('corroboration_type', 'confirm');
    for (const c of corrobs || []) {
      corrobMap[c.contribution_id] = (corrobMap[c.contribution_id] || 0) + 1;
    }
  }

  const results: SearchResult[] = rows.map((row: any) => ({
    id: row.id,
    title: row.title,
    excerpt: row.excerpt,
    body_snippet: row.body ? row.body.substring(0, 180) : null,
    certainty_label: row.certainty_label,
    era_id: row.era_id,
    topic_id: row.topic_id,
    region: row.region,
    word_count: row.word_count,
    created_at: row.created_at,
    author_name: row.is_anonymous ? null : (profileMap[row.author_id] || 'Unknown'),
    is_anonymous: row.is_anonymous,
    status: row.status,
    corroboration_count: corrobMap[row.id] || 0,
    type: 'contribution' as const,
  }));

  return { data: results, total: count || 0 };
}

// ─── Forum Posts ──────────────────────────────────────────────────────────────

export async function searchForumPosts(filters: { query: string; era?: string; page?: number; pageSize?: number }): Promise<{ data: ForumPostResult[]; total: number }> {
  const supabase = createClient();
  const { query, era, page = 1, pageSize = 12 } = filters;
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let q = supabase
    .from('era_forum_posts')
    .select('id, era_id, title, body, author_id, upvote_count, reply_count, created_at', { count: 'exact' })
    .range(from, to);

  if (query.trim()) {
    q = q.or(`title.ilike.%${query}%,body.ilike.%${query}%`);
  }
  if (era) q = q.eq('era_id', era);

  // Trending: order by upvotes + replies + recency
  q = q.order('upvote_count', { ascending: false }).order('reply_count', { ascending: false }).order('created_at', { ascending: false });

  const { data, error, count } = await q;
  if (error) {
    console.error('searchForumPosts error:', error.message);
    return { data: [], total: 0 };
  }

  const rows = data || [];
  const authorIds = [...new Set(rows.filter((r: any) => r.author_id).map((r: any) => r.author_id))];
  let profileMap: Record<string, string> = {};
  if (authorIds.length > 0) {
    const { data: profiles } = await supabase.from('user_profiles').select('id, full_name, username').in('id', authorIds);
    for (const p of profiles || []) profileMap[p.id] = p.full_name || p.username || 'Unknown';
  }

  // Fetch era labels
  const eraIds = [...new Set(rows.map((r: any) => r.era_id).filter(Boolean))];
  let eraMap: Record<string, string> = { ...ERA_LABELS };
  if (eraIds.length > 0) {
    const { data: eras } = await supabase.from('eras').select('id, label').in('id', eraIds);
    for (const e of eras || []) eraMap[e.id] = e.label;
  }

  return {
    data: rows.map((row: any) => ({
      id: row.id,
      era_id: row.era_id,
      era_label: eraMap[row.era_id] || row.era_id,
      title: row.title,
      body_snippet: row.body ? row.body.substring(0, 200) : '',
      author_name: profileMap[row.author_id] || null,
      upvote_count: row.upvote_count || 0,
      reply_count: row.reply_count || 0,
      created_at: row.created_at,
      type: 'forum' as const,
    })),
    total: count || 0,
  };
}

// ─── AMA Questions ────────────────────────────────────────────────────────────

export async function searchAMAQuestions(filters: { query: string; era?: string; page?: number; pageSize?: number }): Promise<{ data: AMAQuestionResult[]; total: number }> {
  const supabase = createClient();
  const { query, era, page = 1, pageSize = 12 } = filters;
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  // First get AMA ids filtered by era if needed
  let amaIds: string[] | null = null;
  if (era) {
    const { data: amas } = await supabase.from('historian_amas').select('id').eq('era_id', era);
    amaIds = (amas || []).map((a: any) => a.id);
    if (amaIds.length === 0) return { data: [], total: 0 };
  }

  let q = supabase
    .from('ama_questions')
    .select('id, ama_id, question, answer, answered_at, upvote_count, is_featured, asker_id, created_at', { count: 'exact' })
    .range(from, to);

  if (query.trim()) {
    q = q.or(`question.ilike.%${query}%,answer.ilike.%${query}%`);
  }
  if (amaIds) q = q.in('ama_id', amaIds);

  // Trending: featured first, then by upvotes, then answered
  q = q.order('is_featured', { ascending: false }).order('upvote_count', { ascending: false }).order('created_at', { ascending: false });

  const { data, error, count } = await q;
  if (error) {
    console.error('searchAMAQuestions error:', error.message);
    return { data: [], total: 0 };
  }

  const rows = data || [];

  // Fetch AMA titles and era info
  const uniqueAmaIds = [...new Set(rows.map((r: any) => r.ama_id))];
  let amaMap: Record<string, { title: string; era_id: string | null }> = {};
  if (uniqueAmaIds.length > 0) {
    const { data: amas } = await supabase.from('historian_amas').select('id, title, era_id').in('id', uniqueAmaIds);
    for (const a of amas || []) amaMap[a.id] = { title: a.title, era_id: a.era_id };
  }

  // Fetch era labels
  const eraIdsFromAmas = [...new Set(Object.values(amaMap).map(a => a.era_id).filter(Boolean))];
  let eraMap: Record<string, string> = { ...ERA_LABELS };
  if (eraIdsFromAmas.length > 0) {
    const { data: eras } = await supabase.from('eras').select('id, label').in('id', eraIdsFromAmas as string[]);
    for (const e of eras || []) eraMap[e.id] = e.label;
  }

  // Fetch asker names
  const askerIds = [...new Set(rows.filter((r: any) => r.asker_id).map((r: any) => r.asker_id))];
  let askerMap: Record<string, string> = {};
  if (askerIds.length > 0) {
    const { data: profiles } = await supabase.from('user_profiles').select('id, full_name, username').in('id', askerIds);
    for (const p of profiles || []) askerMap[p.id] = p.full_name || p.username || 'Unknown';
  }

  return {
    data: rows.map((row: any) => {
      const ama = amaMap[row.ama_id] || { title: 'AMA Session', era_id: null };
      return {
        id: row.id,
        ama_id: row.ama_id,
        ama_title: ama.title,
        era_id: ama.era_id,
        era_label: ama.era_id ? (eraMap[ama.era_id] || ama.era_id) : null,
        question: row.question,
        answer: row.answer,
        answered_at: row.answered_at,
        upvote_count: row.upvote_count || 0,
        is_featured: row.is_featured || false,
        asker_name: askerIds.includes(row.asker_id) ? (askerMap[row.asker_id] || null) : null,
        created_at: row.created_at,
        type: 'ama' as const,
      };
    }),
    total: count || 0,
  };
}

// ─── Contributors ─────────────────────────────────────────────────────────────

export async function searchContributors(filters: { query: string; page?: number; pageSize?: number }): Promise<{ data: ContributorResult[]; total: number }> {
  const supabase = createClient();
  const { query, page = 1, pageSize = 12 } = filters;
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let q = supabase
    .from('user_profiles')
    .select('id, full_name, username, avatar_url, bio, role, tier, reputation_score, published_count, specialisations', { count: 'exact' })
    .eq('is_active', true)
    .range(from, to);

  if (query.trim()) {
    q = q.or(`full_name.ilike.%${query}%,username.ilike.%${query}%,bio.ilike.%${query}%`);
  }

  // Trending: order by reputation + published count
  q = q.order('reputation_score', { ascending: false }).order('published_count', { ascending: false });

  const { data, error, count } = await q;
  if (error) {
    console.error('searchContributors error:', error.message);
    return { data: [], total: 0 };
  }

  return {
    data: (data || []).map((row: any) => ({
      id: row.id,
      full_name: row.full_name || 'Unknown',
      username: row.username,
      avatar_url: row.avatar_url,
      bio: row.bio,
      role: row.role,
      tier: row.tier,
      reputation_score: row.reputation_score || 0,
      published_count: row.published_count || 0,
      specialisations: row.specialisations || [],
      type: 'contributor' as const,
    })),
    total: count || 0,
  };
}

// ─── Wall Topics ──────────────────────────────────────────────────────────────

export async function searchWallTopics(query: string, era?: string): Promise<WallSearchResult[]> {
  const supabase = createClient();

  let topicsQ = supabase
    .from('topics')
    .select('id, era_id, label, min_coverage_threshold');

  if (query.trim()) {
    topicsQ = topicsQ.ilike('label', `%${query}%`);
  }
  if (era) topicsQ = topicsQ.eq('era_id', era);

  const { data: topics, error } = await topicsQ;
  if (error || !topics) return [];

  const topicIds = topics.map((t: any) => t.id);
  if (topicIds.length === 0) return [];

  const { data: counts } = await supabase
    .from('contributions')
    .select('topic_id')
    .eq('status', 'published')
    .in('topic_id', topicIds);

  const countMap: Record<string, number> = {};
  for (const row of counts || []) {
    countMap[row.topic_id] = (countMap[row.topic_id] || 0) + 1;
  }

  const erasQ = await supabase.from('eras').select('id, label');
  const eraMap: Record<string, string> = { ...ERA_LABELS };
  for (const e of erasQ.data || []) {
    eraMap[e.id] = e.label;
  }

  return topics.map((t: any) => ({
    id: t.id,
    era_id: t.era_id,
    era_label: eraMap[t.era_id] || ERA_LABELS[t.era_id] || t.era_id,
    topic_id: t.id,
    topic_label: t.label,
    published: countMap[t.id] || 0,
    min_coverage: t.min_coverage_threshold,
    type: 'wall' as const,
  }));
}

// ─── Trending Results (cross-type) ────────────────────────────────────────────

export async function getTrendingResults(): Promise<{ contributions: SearchResult[]; forumPosts: ForumPostResult[]; amaQuestions: AMAQuestionResult[] }> {
  const [contribRes, forumRes, amaRes] = await Promise.all([
    searchContributions({ query: '', page: 1, pageSize: 6 }),
    searchForumPosts({ query: '', page: 1, pageSize: 4 }),
    searchAMAQuestions({ query: '', page: 1, pageSize: 4 }),
  ]);
  return {
    contributions: contribRes.data,
    forumPosts: forumRes.data,
    amaQuestions: amaRes.data,
  };
}

// ─── Shared helpers ───────────────────────────────────────────────────────────

export async function getErasAndTopics(): Promise<{ eras: { id: string; label: string }[]; topics: { id: string; era_id: string; label: string }[] }> {
  const supabase = createClient();
  const [erasRes, topicsRes] = await Promise.all([
    supabase.from('eras').select('id, label').order('sort_order'),
    supabase.from('topics').select('id, era_id, label').order('label'),
  ]);
  return {
    eras: erasRes.data || [],
    topics: topicsRes.data || [],
  };
}
